import { type FormEvent, useState } from "react";
import { t } from "i18next";
import { toast } from "sonner";
import {
	Link,
	Navigate,
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { z } from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiLink from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { authService, useAuth } from "@/lib/auth";

const resetSearchSchema = z.object({
	token: z.string().optional(),
});

export const Route = createFileRoute("/reset-password")({
	validateSearch: zodValidator(resetSearchSchema),
	component: ResetPasswordPage,
});

export function ResetPasswordPage() {
	const { status, user } = useAuth();
	const navigate = useNavigate();
	const { token } = useSearch({ from: "/reset-password" });

	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [tokenExpired, setTokenExpired] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const errorPanel = (message: string) => (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: "background.level1",
				p: 2,
			}}
		>
			<Paper
				sx={{
					width: { xs: "100%", sm: 420 },
					p: { xs: 3, sm: 4 },
					borderRadius: 2,
				}}
			>
				<Stack spacing={3}>
					<Typography variant="body2" color="error.main">
						{message}
					</Typography>
					<MuiLink
						component={Link as React.ElementType}
						to="/forgot-password"
						variant="body2"
						color="text.secondary"
					>
						{t("Request a new link")}
					</MuiLink>
				</Stack>
			</Paper>
		</Box>
	);

	if (status === "loading") return null;

	if (status === "authenticated" && user) {
		return <Navigate to="/dashboard" replace />;
	}

	if (!token) {
		return errorPanel(t("Invalid reset link. Please request a new one."));
	}

	if (tokenExpired) {
		return errorPanel(
			t("This reset link has expired or is no longer valid. Request a new one.")
		);
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (newPassword.length < 12) {
			toast.error(t("Password must be at least 12 characters"));
			return;
		}
		if (newPassword !== confirmPassword) {
			toast.error(t("Passwords do not match"));
			return;
		}
		setErrorMessage(null);
		setIsSubmitting(true);
		try {
			await authService.resetPassword({ token, newPassword });
			toast.success(t("Password updated. Please log in."));
			await navigate({ to: "/login" });
		} catch (error) {
			const message = error instanceof Error ? error.message : "";
			if (message === "INVALID_OR_EXPIRED_TOKEN") {
				setTokenExpired(true);
			} else if (message === "RATE_LIMITED") {
				setErrorMessage(t("Too many attempts. Please try again later."));
			} else {
				setErrorMessage(t("Something went wrong. Please try again."));
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: "background.level1",
				p: 2,
			}}
		>
			<Paper
				component="form"
				onSubmit={handleSubmit}
				sx={{
					width: { xs: "100%", sm: 420 },
					p: { xs: 3, sm: 4 },
					borderRadius: 2,
				}}
			>
				<Stack spacing={3}>
					<Typography variant="h5">{t("Choose a new password")}</Typography>
					<TextField
						label={t("New password")}
						type="password"
						autoComplete="new-password"
						value={newPassword}
						onChange={(event) => setNewPassword(event.target.value)}
						inputProps={{ minLength: 12 }}
						required
						fullWidth
					/>
					<TextField
						label={t("Confirm new password")}
						type="password"
						autoComplete="new-password"
						value={confirmPassword}
						onChange={(event) => setConfirmPassword(event.target.value)}
						required
						fullWidth
					/>
					{errorMessage && (
						<Typography variant="body2" color="error.main">
							{errorMessage}
						</Typography>
					)}
					<Button
						type="submit"
						variant="contained"
						size="large"
						disabled={isSubmitting}
					>
						{isSubmitting ? t("Saving…") : t("Set new password")}
					</Button>
				</Stack>
			</Paper>
		</Box>
	);
}
